import { validShareReadToken } from '@/utils/share-session-token'
import { locationQuery, MAP_LOCATION_TOPIC } from './device-map-model'
import { DEVICE_STATUS_TOPIC, deviceStatusProductIds } from './device-status-model'
import { fleetQuery, FLEET_TOPIC } from './fleet-observed'

const validStatusProducts = value => { try { return Array.isArray(value) && deviceStatusProductIds({ productIds: value }).length > 0 } catch (_) { return false } }

// 分享专用连接：不复用压缩库中可能属于旧账号的全局连接，只允许原生只读属性主题。
export function createSharedSocket ({ token, authorizationVersion = 1, code, session, current, onInvalidated = () => {}, Socket = window.WebSocket, location = window.location,
  every = setInterval, clear = clearInterval, later = setTimeout, cancel = clearTimeout, onOpen = () => {}, onFailure = () => {} }) {
  let socket, heartbeat, opening, disposed = false
  const subscribers = new Map()
  const send = value => {
    if (!socket || socket.readyState !== 1) return
    try { socket.send(JSON.stringify(value)) } catch (error) { fail('实时消息发送失败，请重试') }
  }
  const stop = () => {
    disposed = true; clear(heartbeat); cancel(opening)
    const connection = socket; socket = null
    if (connection) { connection.onopen = connection.onmessage = connection.onerror = connection.onclose = null; connection.close() }
    subscribers.clear()
  }
  const fail = message => {
    const active = Array.from(subscribers.values()); stop()
    onFailure(message)
    active.forEach(sub => sub.error(new Error(message)))
  }
  const connect = () => {
    if (socket) return
    const delegated = authorizationVersion === 2
    if (![1, 2].includes(authorizationVersion) || (delegated ? !/^[a-f0-9]{64}$/.test(code || '') || !validShareReadToken(session) : !token) || !current() || disposed) throw new Error('分享授权已失效，请重新验证')
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const path = delegated ? `/jetlinks/visualization/share/${code}/messaging?session=${encodeURIComponent(session)}` : `/jetlinks/messaging/${encodeURIComponent(token)}?:X_Access_Token=${encodeURIComponent(token)}`
    socket = new Socket(`${protocol}//${location.host}${path}`)
    opening = later(() => fail('实时通讯连接超时，请重试'), 10000)
    socket.onopen = () => {
      cancel(opening)
      if (!current()) return fail('分享授权已失效，请重新验证')
      onOpen()
      if (disposed) return
      subscribers.forEach(sub => send(sub.request))
      if (disposed) return
      heartbeat = every(() => { if (!current()) fail('分享会话或身份已失效，请重新验证'); else send({ type: 'ping' }) }, 2000)
    }
    socket.onerror = () => fail('实时通讯失败，请重新验证或重试')
    socket.onclose = event => {
      // 仅服务端明确的分享会话撤权码清理整个画布；单设备无权限和断网不是账号失效。
      if (authorizationVersion === 2 && event && event.code === 4001) {
        try { fail('分享授权已失效，请重新验证') } finally { onInvalidated() }
      } else fail('实时通讯已断开，请重试回补数据')
    }
    socket.onmessage = event => {
      if (!current()) return fail('分享会话或身份已失效，请重新验证')
      let response
      try { response = JSON.parse(event.data) } catch (error) { return fail('实时消息格式无效') }
      if (!response || typeof response !== 'object' || Array.isArray(response)) return fail('实时消息格式无效')
      const sub = subscribers.get(response.requestId)
      if (!sub) { if (response.type === 'error') fail('实时通讯验证失败，请重新验证分享'); return }
      if (response.type === 'error') { subscribers.delete(response.requestId); sub.error(new Error(response.message || '没有设备属性订阅权限')) }
      else if (response.type === 'complete') { subscribers.delete(response.requestId); sub.complete() }
      else if (response.type === 'result') sub.next(response)
    }
  }
  return {
    get closed () { return disposed },
    close: stop,
    subscribe (id, topic, parameter) {
      const identifier = '[A-Za-z0-9_-]{1,256}', property = '[A-Za-z0-9_.:\\u4e00-\\u9fff-]{1,256}'
      const latest = new RegExp('^/dashboard/device/' + identifier + '/' + property + '/realTime$').test(topic) && parameter &&
        typeof parameter.deviceId === 'string' && new RegExp('^' + identifier + '$').test(parameter.deviceId) && [0, 1].includes(parameter.history) && Object.keys(parameter).every(key => ['deviceId', 'history'].includes(key))
      const history = new RegExp('^/device/' + identifier + '/' + identifier + '/message/property/\\*\\*$').test(topic) && parameter && !Array.isArray(parameter) && Object.keys(parameter).length === 0
      const group = authorizationVersion === 2 && /^\/visualization\/projects\/[A-Za-z0-9_-]{1,64}\/group-control\/observed$/.test(topic) && parameter && !Array.isArray(parameter) &&
        Object.keys(parameter).length === 2 && ['pageId', 'componentId'].every(key => typeof parameter[key] === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(parameter[key]))
      let locations = false
      if (authorizationVersion === 2 && topic === MAP_LOCATION_TOPIC && parameter && !Array.isArray(parameter)) {
        try { locationQuery(parameter); locations = Object.keys(parameter).every(key => ['scope', 'productIds', 'deviceIds', 'propertyId', 'state'].includes(key)) } catch (_) { /* 不接受任意查询参数 */ }
      }
      const status = authorizationVersion === 2 && topic === DEVICE_STATUS_TOPIC && parameter && !Array.isArray(parameter) &&
        Object.keys(parameter).every(key => ['scope', 'productId', 'productIds', 'pageId', 'componentId'].includes(key)) &&
        ['pageId', 'componentId'].every(key => typeof parameter[key] === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(parameter[key])) &&
        (parameter.scope === 'all' ? parameter.productId === undefined && parameter.productIds === undefined
          : parameter.scope === 'product' ? parameter.productIds === undefined && typeof parameter.productId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(parameter.productId)
            : parameter.scope === 'products' && parameter.productId === undefined && validStatusProducts(parameter.productIds))
      let fleet = false
      if (authorizationVersion === 2 && topic === FLEET_TOPIC) {
        try { fleetQuery(parameter); fleet = !!parameter.pageId && !!parameter.componentId } catch (_) { /* 不接受范围外或额外参数。 */ }
      }
      if ((!latest && !history && !group && !locations && !status && !fleet) || !current() || disposed) throw new Error('分享仅允许授权范围内的设备属性、地图位置、设备统计或群组状态订阅')
      return { subscribe (next, error = () => {}, complete = () => {}) {
        if (!current() || disposed) { error(new Error('分享会话或身份已失效，请重新验证')); return { unsubscribe () {} } }
        const request = { id, topic, parameter, type: 'sub' }
        if (subscribers.has(id)) throw new Error('实时订阅编号重复')
        subscribers.set(id, { request, next, error, complete })
        try { if (socket && socket.readyState === 1) send(request); else connect() } catch (cause) { subscribers.delete(id); stop(); onFailure(cause.message); error(cause) }
        return { unsubscribe () { if (subscribers.delete(id)) send({ id, type: 'unsub' }) } }
      } }
    }
  }
}
